import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { App as CapacitorApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "./AuthContext";
import { translations, type Locale } from "@/i18n/translations";
import { fetch } from "@/lib/api";
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface User {
  id: number;
  username: string;
  fullName: string;
  email?: string;
  profilePicture?: string;
}

interface MoodEntry {
  id: number;
  userId: number;
  moodValue: number;
  fatigueValue: number;
  notes?: string;
  createdAt: Date;
}

interface DailyTask {
  id: number;
  userId: number;
  title: string;
  description: string;
  isCompleted: boolean;
  date: Date;
}

interface AvailableTask {
  title: string;
  description: string;
  isCompleted: boolean;
}

interface TaskHistoryItem {
  date: string;
  completed: number;
  total: number;
}

interface CustomTask {
  title: string;
  description: string;
}

interface AppContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  currentPage: string;
  setCurrentPage: React.Dispatch<React.SetStateAction<string>>;
  panicMode: boolean;
  setPanicMode: React.Dispatch<React.SetStateAction<boolean>>;
  showMoodTracker: boolean;
  setShowMoodTracker: React.Dispatch<React.SetStateAction<boolean>>;
  showTaskCustomizer: boolean;
  setShowTaskCustomizer: React.Dispatch<React.SetStateAction<boolean>>;
  completedTasks: number;
  setCompletedTasks: React.Dispatch<React.SetStateAction<number>>;
  latestMoodEntry: MoodEntry | null;
  refreshMoodEntry: () => void;
  dailyTasks: DailyTask[];
  refreshDailyTasks: () => void;
  taskHistory: TaskHistoryItem[];
  refreshTaskHistory: () => void;
  availableTasks: AvailableTask[];
  getAvailableTasks: () => Promise<void>;
  customizeDailyTasks: (taskIndices: number[], customTasks?: CustomTask[]) => Promise<void>;
  addCustomTask: (title: string, description: string) => Promise<void>;
  toggleTaskCompletion: (taskId: number, isCompleted: boolean) => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>({
    id: 1,
    username: "demo",
    fullName: "João Nascimento",
    email: "joao@alento.app",
    profilePicture: "",
  });
  const [currentPage, setCurrentPage] = useState("home");
  const [panicMode, setPanicMode] = useState(false);
  const [showMoodTracker, setShowMoodTracker] = useState(false);
  const [showTaskCustomizer, setShowTaskCustomizer] = useState(false);
  const [completedTasks, setCompletedTasks] = useState(0);
  const [availableTasks, setAvailableTasks] = useState<AvailableTask[]>([]);
  const { toast } = useToast();
  const {user: authUser} = useAuth();
  
  // Global user-specific app settings
  const [darkMode, setDarkMode] = useState(false);
  const [language, setLanguage] = useState<'en' | 'pt'>('en');
  const [soundEffects, setSoundEffects] = useState(true);
  const [dailyReminders, setDailyReminders] = useState(true);
  const t = (key: string, fallback: string) => translations[language as Locale][key] || fallback; 

  useEffect(() => {
    async function loadSettings() {
      if (!user?.id) return;
      try {
        const res = await fetch('/api/user/settings'); // your API should return user settings
        const data = await res.json();
        setDarkMode(data.darkMode);
        setLanguage(data.language);
        setSoundEffects(data.soundEffects);
        setDailyReminders(data.dailyReminders);
      } catch (error) {
        console.error('Failed to fetch user settings:', error);
      }
    }
    loadSettings();
  }, []);

  // Sync AppContext user with AuthContext user
  useEffect(() => {
    if (authUser) {
      setUser(authUser);
      // sync language preference from server
      if (authUser.locale === 'en' || authUser.locale === 'pt') {
        setLanguage(authUser.locale);
      }
    }
  }, [authUser]);

  // Fetch full user data from backend/database on mount
  useEffect(() => {
    async function loadUser() {
      try {
        const res = await fetch('/api/user'); // your API should return the full user object
        if (res.ok) {
          const data = await res.json();
          setUser(data); // Make sure data includes profilePicture, email, etc!
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    }
    loadUser();
  }, []);

  // Listen for native deep link to trigger Panic Mode
  useEffect(() => {
    let handle: PluginListenerHandle | undefined;
    try {
      CapacitorApp.addListener('appUrlOpen', (data) => {
        try {
          const url = new URL(data.url);
          if (url.protocol === 'alento:' && url.hostname === 'panic') {
            setPanicMode(true);
          }
        } catch {}
      }).then(h => { handle = h; }).catch(() => {});
    } catch {}
    return () => { try { handle?.remove(); } catch {} };
  }, []);

  // Also support web dispatch event from entry if needed
  useEffect(() => {
    const handler = () => setPanicMode(true);
    window.addEventListener('alento:panic', handler as EventListener);
    return () => window.removeEventListener('alento:panic', handler as EventListener);
  }, []);

  // Fetch latest mood entry
  const {
    data: latestMoodEntry,
    refetch: refreshMoodEntry
  } = useQuery({
    queryKey: ["/api/mood/latest", user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) return null;
      const res = await fetch(`/api/mood/latest?userId=${user.id}`);
      if (!res.ok) return null;
      return await res.json();
    }
  });

  // Fetch daily tasks
  const {
    data: dailyTasks = [],
    refetch: refreshDailyTasks
  } = useQuery({
    queryKey: ["/api/tasks", user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) return [];
      const res = await fetch(`/api/tasks?userId=${user.id}`);
      if (!res.ok) return [];
      return await res.json();
    }
  });
  
  // Fetch task history by day
  const {
    data: taskHistory = [],
    refetch: refreshTaskHistory
  } = useQuery({
    queryKey: ["/api/tasks/history", user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) return [];
      const res = await fetch(`/api/tasks/history?userId=${user.id}`);
      if (!res.ok) return [];
      return await res.json();
    }
  });

  // Calculate completed tasks
  useEffect(() => {
    if (dailyTasks && dailyTasks.length > 0) {
      const completed = dailyTasks.filter((task: DailyTask) => task.isCompleted).length;
      setCompletedTasks(completed);
    }
  }, [dailyTasks]);

  // Toggle task completion
  const toggleTaskCompletion = async (taskId: number, isCompleted: boolean) => {
    try {
      const res = await fetch("/api/tasks/toggle", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskId, isCompleted }),
      });

      if (!res.ok) {
        throw new Error("Failed to update task");
      }

      await refreshDailyTasks();
      // Also refresh task history to show updated completion status by day
      await refreshTaskHistory();
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: t('error.failedUpdateTaskCompletionStatus', 'Failed to update task completion status'),
        variant: "destructive",
      });
    }
  };
  
  // Fetch available tasks for customization
  const getAvailableTasks = useCallback(async () => {
    try {
      if (!user) return;
      
      const res = await fetch("/api/tasks/available");
      if (!res.ok) {
        throw new Error("Failed to fetch available tasks");
      }
      
      const tasks = await res.json();
      setAvailableTasks(tasks);
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: t('error.failedFetchAvailableTasks', 'Failed to fetch available tasks'),
        variant: "destructive",
      });
    }
  }, [user, toast]);
  
  // Customize user's daily tasks
  const customizeDailyTasks = async (taskIndices: number[], customTasks: CustomTask[] = []) => {
    try {
      if (!user) return;
      
      const res = await fetch("/api/tasks/customize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          userId: user.id, 
          taskIndices,
          customTasks 
        }),
      });
      
      if (!res.ok) {
        throw new Error("Failed to update daily tasks");
      }
      
      // Refetch daily tasks and history to reflect changes
      await queryClient.refetchQueries({ queryKey: ["/api/tasks", user.id] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/history", user.id] });

      setShowTaskCustomizer(false);
      
      toast({
        title: t('common.success', 'Success'),
        description: t('success.dailyActivitiesUpdated', 'Your daily activities have been updated'),
        variant: "default",
      });
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: t('error.failedCustomizeDailyTasks', 'Failed to customize daily tasks'),
        variant: "destructive",
      });
    }
  };
  
  // Add a custom task directly (without selecting from available tasks)
  const addCustomTask = async (title: string, description: string) => {
    try {
      if (!user) return;
      
      const res = await fetch("/api/tasks/custom", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          userId: user.id, 
          title,
          description
        }),
      });
      
      if (!res.ok) {
        throw new Error("Failed to add custom task");
      }
      
      // Refetch daily tasks to include the new custom task
      await queryClient.refetchQueries({ queryKey: ["/api/tasks", user.id] });
      await queryClient.refetchQueries({ queryKey: ["/api/tasks/history", user.id] });  
      
      toast({
        title: t('common.success', 'Success'),
        description: t('success.customActivityAdded', 'Your custom activity has been added'),
        variant: "default",
      });
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: t('error.failedAddCustomActivity', 'Failed to add custom activity'),
        variant: "destructive",
      });
    }
  };

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        darkMode,
        setDarkMode,
        language,
        setLanguage,
        locale: language,
        t: (key: string, fallback: string = '') => {
          const value = translations[language as Locale]?.[key];
          return value !== undefined ? String(value) : fallback;
        },
        soundEffects,
        setSoundEffects,
        dailyReminders,
        setDailyReminders,
        currentPage,
        setCurrentPage,
        panicMode,
        setPanicMode,
        showMoodTracker,
        setShowMoodTracker,
        showTaskCustomizer,
        setShowTaskCustomizer,
        completedTasks,
        setCompletedTasks,
        latestMoodEntry,
        refreshMoodEntry,
        dailyTasks,
        refreshDailyTasks,
        taskHistory,
        refreshTaskHistory,
        availableTasks,
        getAvailableTasks,
        customizeDailyTasks,
        addCustomTask,
        toggleTaskCompletion,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
}
